import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiSearch, 
  FiEye, 
  FiGrid, 
  FiList,
  FiStar,
  FiClock,
  FiPlay
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import GamePreview from '../../components/Games/GamePreview';
import GamePlayer from '../../components/Games/GamePlayer';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import '../../styles/game-player.css';

const GamesManagement = () => {
  const { api } = useAuth();
  const { showSuccess, showError } = useNotification();

  // State management
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    subject: '',
    gameType: '',
    difficulty: '',
    status: 'all',
    featured: 'all'
  });
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGamePlayer, setShowGamePlayer] = useState(false);
  const [playingGame, setPlayingGame] = useState(null);

  // Game types for categorization
  const gameTypes = [
    { id: 'quiz', name: 'Quiz Games', description: 'Multiple choice and true/false questions' },
    { id: 'puzzle', name: 'Puzzle Games', description: 'Logic and problem-solving challenges' },
    { id: 'memory', name: 'Memory Games', description: 'Memory and recall exercises' },
    { id: 'strategy', name: 'Strategy Games', description: 'Strategic thinking and planning' },
    { id: 'simulation', name: 'Simulation Games', description: 'Real-world scenario simulations' }
  ];

  // Fetch games
  const fetchGames = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 12,
        search: searchQuery,
        ...filters
      });
      
      const response = await api.get(`/games?${params}`);
      // The backend returns both template and custom games
      const allGames = [
        ...(response.data.data.templateGames || []),
        ...(response.data.data.customGames || [])
      ];
      setGames(allGames);
      setTotalPages(response.data.data.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Error fetching games:', err);
      showError('Failed to fetch games');
    } finally {
      setLoading(false);
    }
  }, [api, showError, currentPage, searchQuery, filters]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  // Handle game play
  const handlePlayGame = (game) => {
    setPlayingGame(game);
    setShowGamePlayer(true);
  };

  // Handle score save
  const handleScoreSave = (gameResult) => {
    showSuccess(`Score saved: ${gameResult.score}%`);
    // Optionally refresh games to update play counts
    fetchGames();
  };

  // Close game player
  const closeGamePlayer = () => {
    setShowGamePlayer(false);
    setPlayingGame(null);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Group games by type
  const groupGamesByType = (games) => {
    const grouped = {};
    gameTypes.forEach(type => {
      grouped[type.id] = games.filter(game => game.gameType === type.id);
    });
    // Add ungrouped games
    grouped.other = games.filter(game => !gameTypes.find(type => type.id === game.gameType));
    return grouped;
  };

  if (loading && games.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const groupedGames = groupGamesByType(games);
  const selectClass = 'px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 min-w-[140px]';
  const difficultyBadge = (d) => {
    const c = { beginner: 'bg-emerald-100 text-emerald-800', intermediate: 'bg-amber-100 text-amber-800', advanced: 'bg-red-100 text-red-800' };
    return c[d?.toLowerCase()] || 'bg-slate-100 text-slate-600';
  };

  const renderGameCard = (game) => (
    <div key={game._id || game.id} className={`rounded-lg border border-slate-200 bg-white shadow-sm p-4 transition-colors hover:border-slate-300 hover:shadow-md ${viewMode === 'list' ? 'flex items-center gap-4 flex-wrap' : ''}`}>
      <div className={`flex justify-between items-start gap-2 ${viewMode === 'list' ? 'flex-[0_0_200px] min-w-0' : 'mb-3'}`}>
        <h3 className="text-base font-semibold text-slate-900 leading-tight truncate">{game.title}</h3>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => { setSelectedGame(game); setShowPreviewModal(true); }} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Preview"><FiEye className="h-4 w-4" /></button>
          <button type="button" onClick={() => handlePlayGame(game)} className="p-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors" title="Play"><FiPlay className="h-4 w-4" /></button>
        </div>
      </div>
      {viewMode === 'list' && <p className="text-sm text-slate-600 flex-1 min-w-0 line-clamp-2">{game.description}</p>}
      {viewMode === 'grid' && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{game.description}</p>}
      <div className="flex flex-wrap gap-2 mb-3">
        {game.subject && <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium border border-slate-200 bg-white text-slate-700">{game.subject}</span>}
        {game.difficulty && <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${difficultyBadge(game.difficulty)}`}>{game.difficulty}</span>}
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><FiPlay className="h-3.5 w-3.5" />{game.analytics?.totalPlays || 0} plays</span>
        <span className="inline-flex items-center gap-1"><FiStar className="h-3.5 w-3.5" />{game.averageRating || 0}/5</span>
        <span className="inline-flex items-center gap-1"><FiClock className="h-3.5 w-3.5" />{game.metadata?.estimatedDuration || 15} min</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900">Games</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage and play educational games.</p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 mb-6">
          <h2 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Settings</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block text-slate-600 font-medium mb-1">Template games</label>
              <p className="text-slate-500">Quiz Rush, Memory Match, Word Builder, Formula Race, and Chain Reaction are available to all students.</p>
            </div>
            <div>
              <label className="block text-slate-600 font-medium mb-1">Custom games</label>
              <p className="text-slate-500">Teachers and admins can create custom games from the Games page.</p>
            </div>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-0">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" placeholder="Search games..." value={searchQuery} onChange={handleSearch} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={filters.subject || ''} onChange={(e) => handleFilterChange('subject', e.target.value)} className={selectClass}>
              <option value="">All subjects</option>
              <option value="mathematics">Mathematics</option>
              <option value="english">English</option>
              <option value="physics">Physics</option>
              <option value="chemistry">Chemistry</option>
              <option value="biology">Biology</option>
              <option value="geography">Geography</option>
              <option value="economics">Economics</option>
              <option value="government">Government</option>
              <option value="literature">Literature</option>
            </select>
            <select value={filters.gameType || ''} onChange={(e) => handleFilterChange('gameType', e.target.value)} className={selectClass}>
              <option value="">All types</option>
              {gameTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
            </select>
            <select value={filters.difficulty || ''} onChange={(e) => handleFilterChange('difficulty', e.target.value)} className={selectClass}>
              <option value="">All difficulties</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button type="button" onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`} title="Grid"><FiGrid className="h-4 w-4" /></button>
              <button type="button" onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`} title="List"><FiList className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {gameTypes.map(gameType => {
          const categoryGames = groupedGames[gameType.id];
          if (!categoryGames || categoryGames.length === 0) return null;
          return (
            <div key={gameType.id} className="mb-8">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4 pb-2 border-b-2 border-slate-200">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">{gameType.name}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">{gameType.description}</p>
                </div>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-900 text-white">{categoryGames.length}</span>
              </div>
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : 'flex flex-col gap-3'}>
                {categoryGames.map(renderGameCard)}
              </div>
            </div>
          );
        })}

        {groupedGames.other && groupedGames.other.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4 pb-2 border-b-2 border-slate-200">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Other Games</h2>
                <p className="text-sm text-slate-500 mt-0.5">Miscellaneous educational games</p>
              </div>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-900 text-white">{groupedGames.other.length}</span>
            </div>
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : 'flex flex-col gap-3'}>
              {groupedGames.other.map(renderGameCard)}
            </div>
          </div>
        )}

        {games.length === 0 && !loading && (
          <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
            <FiGrid className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No games found. Try adjusting search or filters.</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-2 py-3 rounded-lg border border-slate-200 bg-white shadow-sm mt-4">
            <span className="text-xs text-slate-500">Page {currentPage} of {totalPages}</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Previous</button>
              <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Next</button>
            </div>
          </div>
        )}

        <Modal isOpen={showPreviewModal} onClose={() => { setShowPreviewModal(false); setSelectedGame(null); }} title="Game Preview" size="large">
          {selectedGame && (
            <GamePreview game={selectedGame} onPlay={() => { setShowPreviewModal(false); handlePlayGame(selectedGame); }} />
          )}
        </Modal>

        {showGamePlayer && playingGame && (
          <div className="fixed inset-0 z-[1000] overflow-y-auto bg-slate-950">
            <GamePlayer game={playingGame} onClose={closeGamePlayer} onScoreSave={handleScoreSave} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GamesManagement;